import { Injectable } from '@angular/core';
import {
  TixAddOrderToHoldGQL,
  TixGetOtherOrdersOnHoldGQL,
  TixRemoveOrderFromHoldGQL,
  TixTicketOnHoldInput
} from '@tix/data-access';
import { LoggerService } from 'apps/purchasing-portal/src/app/logger.service';

@Injectable({
  providedIn: 'root'
})
export class TicketHoldService {
  constructor(
    private addOrderToHoldMutation: TixAddOrderToHoldGQL,
    private removeOrderFromHoldMutation: TixRemoveOrderFromHoldGQL,
    private getOtherOrdersOnHoldQuery: TixGetOtherOrdersOnHoldGQL,
    private logger: LoggerService
  ) {}

  public async getTicketsOnHoldByOtherUsers(
    userSessionId: string,
    ticketConfigIds: string[]
  ) {
    try {
      this.logger.info({
        message: 'Checking tickets on hold by other users',
        event: 'tickets_on_hold_check_start',
        data: { userSessionId, ticketCount: ticketConfigIds.length }
      });

      const response = await this.getOtherOrdersOnHoldQuery
        .fetch(
          { tickets: ticketConfigIds, userSessionId },
          { fetchPolicy: 'no-cache' }
        )
        .toPromise();

      const ticketsOnHold = response.data.getOtherUsersOnHoldTickets;
      this.logger.info({
        message: 'Successfully retrieved tickets on hold by other users',
        event: 'tickets_on_hold_check_success',
        data: {
          userSessionId,
          ticketCount: ticketConfigIds.length,
          ticketsOnHoldCount: ticketsOnHold.length
        }
      });

      return ticketsOnHold;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      this.logger.error({
        message: 'Failed to check tickets on hold by other users',
        event: 'tickets_on_hold_check_error',
        data: {
          userSessionId,
          ticketCount: ticketConfigIds.length,
          error: errorMessage
        }
      });
      throw error;
    }
  }

  public async addOrderToHoldForUserSession(
    userSessionId: string,
    tickets: TixTicketOnHoldInput[]
  ) {
    try {
      this.logger.info({
        message: 'Adding order to hold',
        event: 'order_hold_add_start',
        data: { userSessionId, ticketCount: tickets.length }
      });

      const response = await this.addOrderToHoldMutation
        .mutate({ userSessionId, tickets })
        .toPromise();

      if (!response.data?.addOrderToHold) {
        this.logger.error({
          message: 'Failed to add order to hold - No response data',
          event: 'order_hold_add_failure',
          data: { userSessionId, ticketCount: tickets.length }
        });
        throw new Error('Failed to add the order to hold');
      }

      this.logger.info({
        message: 'Successfully added order to hold',
        event: 'order_hold_add_success',
        data: { userSessionId, ticketCount: tickets.length }
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      this.logger.error({
        message: 'Failed to add order to hold',
        event: 'order_hold_add_error',
        data: {
          userSessionId,
          ticketCount: tickets.length,
          error: errorMessage
        }
      });
      throw error;
    }
  }

  public async removeOrderFromHoldForUserSession(userSessionId: string) {
    try {
      this.logger.info({
        message: 'Removing order from hold',
        event: 'order_hold_remove_start',
        data: { userSessionId }
      });

      const response = await this.removeOrderFromHoldMutation
        .mutate({ userSessionId })
        .toPromise();

      if (!response.data?.removeOrderFromHold) {
        this.logger.error({
          message: 'Failed to remove order from hold - No response data',
          event: 'order_hold_remove_failure',
          data: { userSessionId }
        });
        throw new Error('Failed to remove the order from hold');
      }

      this.logger.info({
        message: 'Successfully removed order from hold',
        event: 'order_hold_remove_success',
        data: { userSessionId }
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      this.logger.error({
        message: 'Failed to remove order from hold',
        event: 'order_hold_remove_error',
        data: { userSessionId, error: errorMessage }
      });
      throw error;
    }
  }
}
